import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      retry: 1,
    },
  },
});

// The router needs an `auth` slot in its context so beforeLoad can guard
// routes (spec §15.9). The actual value is injected per-render below via
// <RouterProvider context={...} />. The `null!` cast is a typed placeholder
// so TypeScript can resolve context.auth everywhere; it's never read at
// runtime because the provider always overrides it.
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: null as unknown as ReturnType<typeof useAuth>,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ queryClient, auth }} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}
